import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import type { BoardViewportState } from "../../lib/board/boardViewport";
import { worldToClient } from "../../lib/board/boardViewport";
import { objectBounds } from "../../editor/selectionBounds";
import {
  MAX_COMMENT_TEXT_LENGTH,
  readObjectComments,
  type ObjectComment,
} from "../../editor/objectComments";
import type { WbBoardScene } from "../../lib/board/wb-board-scene";

type Props = {
  objectId: string;
  scene: InstanceType<typeof WbBoardScene>;
  viewportRef: RefObject<HTMLDivElement | null>;
  viewport: BoardViewportState;
  layoutTick: number;
  sceneTick?: number;
  canEdit: boolean;
  authorLabel: string;
  authorPeerId?: string | null;
  onClose: () => void;
};

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function ObjectCommentsPanel({
  objectId,
  scene,
  viewportRef,
  viewport,
  layoutTick,
  sceneTick = 0,
  canEdit,
  authorLabel,
  authorPeerId,
  onClose,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const obj = scene.findObject(objectId);
  const comments: ObjectComment[] = useMemo(() => {
    void sceneTick;
    return readObjectComments(obj ?? undefined);
  }, [obj, sceneTick, objectId]);

  const pos = useMemo(() => {
    void layoutTick;
    const vpEl = viewportRef.current;
    if (!obj || !vpEl) return null;
    const b = objectBounds(obj);
    const rect = vpEl.getBoundingClientRect();
    const anchor = worldToClient(b.x + Math.max(b.w, 40), b.y + b.h / 2, rect, viewport);
    return { left: anchor.x + 12, top: anchor.y };
  }, [obj, viewport, viewportRef, layoutTick]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [objectId]);

  useEffect(() => {
    if (!obj) onClose();
  }, [obj, onClose]);

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") {
        ev.preventDefault();
        onClose();
      }
    }
    function onDoc(ev: MouseEvent) {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(ev.target as Node)) return;
      onClose();
    }
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDoc);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [onClose]);

  const submit = useCallback(async () => {
    if (!canEdit || busy) return;
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      await scene.addObjectComment(objectId, text, authorLabel, authorPeerId ?? undefined);
      setDraft("");
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }, [canEdit, busy, draft, scene, objectId, authorLabel, authorPeerId]);

  const remove = useCallback(
    async (commentId: string) => {
      if (!canEdit || busy) return;
      setBusy(true);
      try {
        await scene.deleteObjectComment(objectId, commentId);
      } finally {
        setBusy(false);
      }
    },
    [canEdit, busy, scene, objectId]
  );

  if (!obj || !pos) return null;

  return (
    <div
      ref={rootRef}
      className="wb-object-comments"
      role="dialog"
      aria-label="Комментарии к объекту"
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        transform: "translateY(-50%)",
        zIndex: 220,
      }}
      onMouseDown={(ev) => ev.stopPropagation()}
    >
      <header className="wb-object-comments__head">
        <span className="wb-object-comments__title">Комментарии</span>
        <button
          type="button"
          className="wb-object-comments__close"
          aria-label="Закрыть"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      <ul className="wb-object-comments__list">
        {comments.length === 0 ? (
          <li className="wb-object-comments__empty">Пока нет вопросов — напишите первый</li>
        ) : (
          comments.map((c) => {
            const mine =
              (authorPeerId && c.authorPeerId && c.authorPeerId === authorPeerId) ||
              (!c.authorPeerId && c.authorLabel === authorLabel);
            return (
              <li key={c.id} className="wb-object-comments__item">
                <div className="wb-object-comments__meta">
                  <strong>{c.authorLabel}</strong>
                  <span>{formatTime(c.createdAt)}</span>
                </div>
                <p className="wb-object-comments__text">{c.text}</p>
                {canEdit && mine ? (
                  <button
                    type="button"
                    className="wb-object-comments__delete"
                    onClick={() => void remove(c.id)}
                    disabled={busy}
                  >
                    Удалить
                  </button>
                ) : null}
              </li>
            );
          })
        )}
      </ul>

      {canEdit ? (
        <div className="wb-object-comments__composer">
          <textarea
            ref={inputRef}
            className="wb-object-comments__input"
            rows={2}
            maxLength={MAX_COMMENT_TEXT_LENGTH}
            placeholder="Вопрос или ответ…"
            value={draft}
            onChange={(ev) => setDraft(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === "Enter" && !ev.shiftKey) {
                ev.preventDefault();
                void submit();
              }
            }}
          />
          <button
            type="button"
            className="wb-btn wb-btn--sm"
            disabled={busy || !draft.trim()}
            onClick={() => void submit()}
          >
            Отправить
          </button>
        </div>
      ) : (
        <p className="wb-object-comments__readonly">Только просмотр</p>
      )}
    </div>
  );
}
